import React, { useState, useEffect } from "react";
import {
  FiCopy,
  FiCheck,
  FiGlobe,
  FiKey,
  FiCode,
  FiAlertCircle,
  FiShield,
} from "react-icons/fi";
import EmbedInstaller from "../../EmbedInstaller/EmbedInstaller";
import { postRequestAuthenticated } from "../../../services/rest";

// Per-mode, copyable integration guides so a business can wire up whichever
// sign-in method they picked in Settings → Authentication.
const AUTH_GUIDES = {
  otp: {
    label: "Email OTP",
    blurb:
      "Members enter their email and get a one-time code. Nothing to build on your side — just install the embed snippet above; members sign in inside the embed.",
    code: null,
  },
  google: {
    label: "Google sign-in",
    blurb:
      "Members sign in with Google in a popup. Nothing to build on your side — just install the embed snippet above.",
    code: null,
  },
  jwt: {
    label: "JWT SSO",
    blurb:
      "Your server signs a short-lived token that says who the member is; we verify the signature and log them in automatically. Most secure — use when your app already has logged-in users.",
    note:
      "How it works: your backend already knows the logged-in user. It signs a small token (using your Signing secret from Settings → Authentication → Signing secret) that carries their identity, and hands it to the widget in the browser. We verify the signature with the same secret and sign that member in — no email code, no popup. " +
      "Identity: put ANY ONE of external_id (your stable user id — best), email, or phone in the token. Whichever you use is how we match the member on every visit. name and image are optional (just their profile). " +
      "Keep the Signing secret on your server only — never ship it to the browser.",
    code: (key) => `// ── On YOUR server (secret stays here, never in the browser) ──
import jwt from "jsonwebtoken";
const token = jwt.sign(
  {
    // identify the member with ANY ONE of these (whichever you key users on):
    external_id: user.id,   // your stable user id (recommended)
    email: user.email,      //   …or
    phone: user.phone,      //   …or  (e.g. +14155550123)
    // optional profile fields:
    name: user.name,
    image: user.avatar,
  },
  process.env.CHANNELS_EMBED_SECRET,   // = your Signing secret (Settings → Authentication)
  { algorithm: "HS256", expiresIn: "5m" }
);

// ── In the browser, hand that token to the widget ──
ChannelsWidget({ apiKey: "${key}" });
openChannelsWidget({ token: tokenFromYourServer, selectedChannel: "general" });`,
  },
  trusted: {
    label: "Trusted passthrough",
    blurb:
      "You pass the member's details straight to the widget and we trust them as-is — no token, no verification. Use only on pages already behind your own login.",
    note:
      "Simplest option: you send the member's details from the browser and we take them at face value (no signature). Because there's nothing to verify, only use it on pages that already sit behind your own auth. " +
      "Identity: pass ANY ONE of external_id (your stable user id — best), email, or phone; that's what we match the member on (first visit creates them, later visits reuse them). name and image are optional. " +
      "Difference from JWT: JWT is signed on your server so it can't be tampered with; trusted just passes the raw fields — easier to set up, less secure.",
    code: (key) => `ChannelsWidget({ apiKey: "${key}" });
openChannelsWidget({
  // identify the member with ANY ONE of these:
  external_id: user.id,     // your stable user id (recommended)
  email: user.email,        //   …or
  phone: user.phone,        //   …or  (e.g. +14155550123)
  // optional:
  name: user.name,
  image: user.avatar,
  autoLogin: true,
  selectedChannel: "general",
});`,
  },
};

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  return (
    <div className="relative">
      <button
        onClick={copy}
        className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded-md border border-theme-chatDivider bg-theme-secondaryBackground px-2 py-1 text-xs font-medium text-theme-primaryText"
      >
        {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
        {copied ? "Copied" : "Copy"}
      </button>
      <pre className="overflow-x-auto rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground p-3 pr-16 text-xs leading-relaxed text-theme-secondaryText">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const AuthExamples = ({ apiKey }) => {
  const [mode, setMode] = useState("otp");
  const [current, setCurrent] = useState(null);
  useEffect(() => {
    let active = true;
    postRequestAuthenticated("/embed/config", {})
      .then((res) => {
        if (active && res?.success && res.config?.auth?.mode) {
          setCurrent(res.config.auth.mode);
          setMode(res.config.auth.mode);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  const key = apiKey || "YOUR_API_KEY";
  const g = AUTH_GUIDES[mode];
  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-2">
        {Object.entries(AUTH_GUIDES).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setMode(k)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              mode === k
                ? "border-theme-accent text-theme-accent"
                : "border-theme-chatDivider text-theme-primaryText hover:bg-theme-tertiaryBackground"
            }`}
          >
            {v.label}
            {current === k ? " · current" : ""}
          </button>
        ))}
      </div>
      <p className="mb-3 text-xs text-theme-placeholder">{g.blurb}</p>
      {g.code && <CodeBlock code={g.code(key)} />}
      {g.note && (
        <div className="mt-3 rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-xs leading-relaxed text-theme-placeholder">
          {g.note}
        </div>
      )}
      {current && mode !== current && (
        <p className="mt-2 text-xs text-theme-placeholder">
          Your community is currently set to{" "}
          <span className="font-semibold text-theme-secondaryText">
            {AUTH_GUIDES[current]?.label}
          </span>
          . Change it in Settings → Authentication.
        </p>
      )}
    </div>
  );
};

const CopyField = ({ value, mono = true }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!value) return;
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="flex items-stretch gap-2">
      <div
        className={`flex-1 truncate rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText ${
          mono ? "font-mono" : ""
        }`}
      >
        {value || "—"}
      </div>
      <button
        onClick={copy}
        disabled={!value}
        className="flex items-center gap-1.5 rounded-lg border border-theme-chatDivider px-3 py-2.5 text-xs font-semibold text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground disabled:opacity-40"
      >
        {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const Card = ({ icon: Icon, title, subtitle, children }) => (
  <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
    <div className="mb-3 flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-theme-tertiaryBackground text-theme-accent">
        <Icon size={16} />
      </span>
      <div>
        <h3 className="text-sm font-semibold text-theme-secondaryText">{title}</h3>
        {subtitle && (
          <p className="text-xs text-theme-placeholder">{subtitle}</p>
        )}
      </div>
    </div>
    {children}
  </div>
);

// Generate a ready-to-share link that opens the hosted embed — optionally
// straight into a specific channel (the embed resolves ?channel= by name).
const IntegrationTab = ({ business }) => {
  const apiKey = business?.apiKey;
  const domain = business?.domain;
  const isVerified = business?.isVerified;

  return (
    <div className="mx-auto max-w-4xl px-4 py-7 sm:px-6 lg:px-8">
      <div className="mb-7">
        <h1 className="text-2xl font-semibold text-theme-secondaryText">
          Integration &amp; API
        </h1>
        <p className="mt-1 text-sm text-theme-placeholder">
          Your API key, embed snippet, and allowed domain.
        </p>
      </div>

      <div className="flex flex-col gap-5">
        {apiKey ? (
          <Card
            icon={FiKey}
            title="Public API key"
            subtitle="Safe to use in your site's client-side embed."
          >
            <CopyField value={apiKey} />
          </Card>
        ) : (
          <div className="flex items-center gap-3 rounded-xl border border-theme-chatDivider bg-theme-secondaryBackground px-4 py-3 text-sm text-theme-placeholder">
            <FiAlertCircle className="flex-shrink-0 text-theme-warning" size={18} />
            <span>
              Verify your domain to generate your public API key. You can preview
              and configure the embed below in the meantime.
            </span>
          </div>
        )}

        <Card
          icon={FiCode}
          title="Embed snippet"
          subtitle="Pick your platform and stack, then paste the snippet."
        >
          <EmbedInstaller apiKey={apiKey} />
        </Card>

        <Card
          icon={FiShield}
          title="Authentication & sign-in"
          subtitle="How members sign in — copy the example for your chosen mode."
        >
          <AuthExamples apiKey={apiKey} />
        </Card>

        {apiKey && (
          <Card icon={FiGlobe} title="Allowed domain">
            <div className="flex items-center justify-between gap-3 rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5">
              <span className="font-mono text-sm text-theme-secondaryText">
                {domain || "—"}
              </span>
              {isVerified && (
                <span className="flex items-center gap-1 rounded-full border border-theme-success px-2 py-0.5 text-xs font-semibold text-theme-success">
                  <FiCheck size={12} /> Verified
                </span>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default IntegrationTab;
